import React, { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = "https://mnkrsdhadxwinwcipwip.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1ua3JzZGhhZHh3aW53Y2lwd2lwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI1NTE3MzYsImV4cCI6MjA4ODEyNzczNn0.caya9o3h4bJooHRZAKo9FpaI2VbnZglYblSEadGuCX0";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const CHANNELS = [
  { id: "top", label: "Top", color: "#60a5fa" },
  { id: "world", label: "World", color: "#3b82f6" },
  { id: "us", label: "U.S.", color: "#6366f1" },
  { id: "politics", label: "Politics", color: "#a855f7" },
  { id: "business", label: "Business", color: "#8b5cf6" },
  { id: "markets", label: "Markets", color: "#eab308" },
  { id: "economy", label: "Economy", color: "#f59e0b" },
  { id: "tech", label: "Tech", color: "#06b6d4" },
  { id: "ai", label: "AI", color: "#22d3ee" },
  { id: "cyber", label: "Cyber", color: "#0ea5e9" },
  { id: "science", label: "Science", color: "#14b8a6" },
  { id: "real-estate", label: "Real Estate", color: "#fb7185" },
  { id: "sports", label: "Sports", color: "#f97316" },
  { id: "nhl", label: "NHL", color: "#0ea5e9" },
  { id: "climate", label: "Climate", color: "#10b981" },
  { id: "bright", label: "Bright Side", color: "#fbbf24" },
  { id: "live-event", label: "Live", color: "#ef4444" },
];
const FEED_CHANNELS = CHANNELS.filter((c) => c.id !== "top");

const MISSION = {
  bullets: [
    "A feed built for information, not engagement.",
    "No likes. No comments. No creators chasing clicks.",
    "Each item includes sources and a confidence score.",
    '"Confirmed" means multiple independent sources matched the same story.',
    '"Developing" means a single source or incomplete corroboration.',
    "No opinion. Structured summaries of reported facts.",
    "Corrections happen on the next engine cycle.",
  ],
  footnote: "ReadOnly is an information layer. Always verify directly with the linked sources.",
};

function heatColor(v) {
  if (v >= 90) return "#ef4444";
  if (v >= 80) return "#f97316";
  if (v >= 65) return "#f59e0b";
  return "#3b82f6";
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return mins + "m ago";
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + "h ago";
    return Math.floor(hrs / 24) + "d ago";
  } catch { return ""; }
}

function normalizeTitle(s) {
  return (s || "").toLowerCase().replace(/['']/g, "").replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

function topicKeyFromTitle(title) {
  const stop = new Set(["the","a","an","and","or","to","of","in","on","for","with","as","at","by","from","into","amid","after","before","over","up","down","new","live","update","updates","breaking","says","say","report","reports","reported"]);
  return normalizeTitle(title).split(" ").filter(t => t && t.length > 2 && !stop.has(t)).slice(0, 8).join("-");
}

function getSourceUrl(fact, index) {
  const links = Array.isArray(fact.links) ? fact.links : [];
  const url = links[index];
  return (url && typeof url === "string" && url.startsWith("http")) ? url : null;
}

function dedup(items) {
  const out = [];
  for (const item of items) {
    const words = new Set(normalizeTitle(item.title).split(" ").filter(Boolean));
    let isDupe = false;
    for (const kept of out) {
      const kw = new Set(normalizeTitle(kept.title).split(" ").filter(Boolean));
      let inter = 0; for (const w of words) if (kw.has(w)) inter++;
      if ((words.size + kw.size - inter) > 0 && inter / (words.size + kw.size - inter) >= 0.45) { isDupe = true; break; }
    }
    if (!isDupe) out.push(item);
  }
  return out;
}

async function shareFact(fact) {
  const title = fact.title || "";
  const url = getSourceUrl(fact, 0) || "https://readonly.news";
  const text = title + "\n\nvia ReadOnly \u2014 readonly.news";
  if (navigator.share) {
    try { await navigator.share({ title: "ReadOnly", text, url }); return; } catch {}
  }
  try { await navigator.clipboard.writeText(text + "\n" + url); alert("Copied to clipboard!"); } catch {}
}

const CSS = `
  :root {
    --bg-0: #06060a; --bg-1: #0d0d14; --bg-2: #131320; --bg-3: #1a1a2a;
    --border: #1c1c2e; --border-light: #28283e;
    --text-0: #f0f0f5; --text-1: #c8c8d8; --text-2: #8888a0; --text-3: #55556e;
    --green: #22c55e; --amber: #f59e0b; --red: #ef4444; --blue: #3b82f6;
    --mono: 'JetBrains Mono', monospace; --sans: 'DM Sans', sans-serif;
    --safe-bottom: env(safe-area-inset-bottom, 0px);
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body, #root { height: 100%; }
  body { background: var(--bg-0); color: var(--text-0); font-family: var(--sans); -webkit-font-smoothing: antialiased; overflow: hidden; }
  #root { display: flex; flex-direction: column; }
  ::-webkit-scrollbar { width: 0; height: 0; }
  .shell { display: flex; flex-direction: column; height: 100%; }

  .top-header { padding: 14px 20px 0; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
  .brand { font-family: var(--mono); font-size: 13px; font-weight: 700; letter-spacing: 3px; color: var(--text-0); }
  .brand span { color: var(--blue); }
  .sync-badge { font-family: var(--mono); font-size: 9px; color: var(--text-3); display: flex; align-items: center; gap: 5px; }
  .sync-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--green); animation: pulse 2s infinite; }

  .ch-dropdown-wrap { padding: 12px 20px 14px; flex-shrink: 0; position: relative; }
  .ch-dropdown-btn { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 12px 16px; background: var(--bg-1); border: 1px solid var(--border); border-radius: 10px; color: var(--text-0); font-family: var(--sans); font-size: 15px; font-weight: 600; cursor: pointer; -webkit-tap-highlight-color: transparent; transition: border-color 0.2s, transform 0.1s; }
  .ch-dropdown-btn:active { border-color: var(--border-light); transform: scale(0.99); }
  .ch-label-row { display: flex; align-items: center; gap: 10px; }
  .ch-color-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .ch-fact-count { font-family: var(--mono); font-size: 10px; color: var(--text-3); }
  .ch-arrow { font-size: 12px; color: var(--text-3); transition: transform 0.2s; }
  .ch-arrow.open { transform: rotate(180deg); }
  .ch-dropdown-menu { position: absolute; top: calc(100% - 4px); left: 20px; right: 20px; background: var(--bg-1); border: 1px solid var(--border); border-radius: 10px; z-index: 50; max-height: min(62vh, 520px); overflow-y: auto; -webkit-overflow-scrolling: touch; padding-bottom: 8px; animation: fadeDown 0.15s ease; box-shadow: 0 16px 48px rgba(0,0,0,0.5); }
  .ch-dropdown-menu::-webkit-scrollbar { width: 6px; }
  .ch-dropdown-menu::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 8px; }
  .ch-dropdown-item { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid var(--border); cursor: pointer; -webkit-tap-highlight-color: transparent; transition: background 0.1s; }
  .ch-dropdown-item:last-child { border-bottom: none; }
  .ch-dropdown-item:active, .ch-dropdown-item.active { background: var(--bg-2); }
  .ch-dropdown-item .ch-label-row { font-size: 15px; font-weight: 500; }

  .scroll-body { flex: 1; overflow-y: auto; overflow-x: hidden; -webkit-overflow-scrolling: touch; padding-bottom: calc(72px + var(--safe-bottom)); scroll-behavior: smooth; }
  .section-label { font-family: var(--mono); font-size: 10px; font-weight: 600; letter-spacing: 1.5px; color: var(--text-3); text-transform: uppercase; padding: 16px 20px 10px; }

  /* CARDS — tactile feel */
  .fact-card { padding: 18px 20px; border-bottom: 1px solid var(--border); cursor: pointer; -webkit-tap-highlight-color: transparent; transition: background 0.15s, transform 0.1s; }
  .fact-card:active { background: var(--bg-1); transform: scale(0.995); }
  .fact-card.expanded { background: var(--bg-1); }
  .fact-tags { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; flex-wrap: wrap; }
  .tag { font-family: var(--mono); font-size: 9px; font-weight: 600; letter-spacing: 0.8px; padding: 3px 8px; border-radius: 3px; text-transform: uppercase; }
  .tag-confirmed { background: rgba(34,197,94,0.1); color: var(--green); }
  .tag-developing { background: rgba(245,158,11,0.1); color: var(--amber); }
  .tag-ch { background: rgba(59,130,246,0.08); color: var(--blue); }
  .fact-title { font-size: 17px; font-weight: 600; line-height: 1.38; color: var(--text-0); margin-bottom: 6px; }
  .fact-snippet { font-size: 14px; line-height: 1.55; color: var(--text-2); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .fact-meta { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; margin-top: 8px; }

  .src-chip { font-family: var(--mono); font-size: 9px; font-weight: 500; color: var(--text-2); text-decoration: none; padding: 3px 7px; background: var(--bg-2); border: 1px solid var(--border); border-radius: 4px; display: inline-flex; align-items: center; gap: 3px; transition: all 0.15s; -webkit-tap-highlight-color: transparent; }
  a.src-chip:hover, a.src-chip:active { color: var(--blue); border-color: var(--border-light); background: var(--bg-3); }
  .meta-time { font-family: var(--mono); font-size: 9px; color: var(--text-3); margin-left: auto; }

  /* DETAIL panel */
  .fact-detail { margin-top: 14px; padding: 14px 16px; background: var(--bg-2); border: 1px solid var(--border); border-radius: 8px; animation: fadeUp 0.2s ease; }
  .detail-label { font-family: var(--mono); font-size: 9px; font-weight: 600; letter-spacing: 1.5px; color: var(--text-3); text-transform: uppercase; margin-bottom: 6px; margin-top: 14px; }
  .detail-label:first-child { margin-top: 0; }
  .fact-list { list-style: none; }
  .fact-list li { font-size: 14px; line-height: 1.55; color: var(--text-1); padding: 4px 0 4px 16px; position: relative; }
  .fact-list li::before { content: '\u25B8'; position: absolute; left: 0; color: var(--text-3); font-size: 11px; }
  .detail-ctx { font-size: 14px; line-height: 1.6; color: var(--text-2); }
  .detail-intel { font-family: var(--mono); font-size: 9px; color: var(--text-3); line-height: 2; }
  .detail-sources { display: flex; flex-direction: column; gap: 6px; }
  .detail-src-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: var(--bg-1); border: 1px solid var(--border); border-radius: 8px; text-decoration: none; transition: border-color 0.15s, transform 0.1s; -webkit-tap-highlight-color: transparent; }
  a.detail-src-row:active { border-color: var(--border-light); transform: scale(0.99); }
  .detail-src-name { font-size: 13px; font-weight: 600; color: var(--text-0); }
  .detail-src-arrow { font-family: var(--mono); font-size: 12px; color: var(--blue); font-weight: 700; }

  /* SHARE BUTTON */
  .share-btn { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; margin-top: 14px; padding: 12px; background: var(--bg-1); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-family: var(--mono); font-size: 11px; font-weight: 600; letter-spacing: 0.5px; cursor: pointer; -webkit-tap-highlight-color: transparent; transition: all 0.15s; }
  .share-btn:active { background: var(--bg-3); transform: scale(0.98); color: var(--text-0); border-color: var(--border-light); }

  /* BRIEFING */
  .brief-head { padding: 18px 20px 10px; }
  .brief-title { font-size: 20px; font-weight: 700; color: var(--text-0); margin-bottom: 6px; }
  .brief-sub { font-family: var(--mono); font-size: 10px; color: var(--text-3); letter-spacing: 0.4px; line-height: 1.8; }
  .brief-card { margin: 0 20px 12px; padding: 16px 18px; background: linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015)); border: 1px solid var(--border); border-radius: 12px; cursor: pointer; -webkit-tap-highlight-color: transparent; position: relative; overflow: hidden; transition: transform 0.1s; }
  .brief-card:active { border-color: var(--border-light); transform: scale(0.99); }
  .brief-rank { font-family: var(--mono); font-size: 11px; font-weight: 700; color: var(--text-3); }
  .brief-toprow { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; gap: 10px; }
  .brief-pill-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .brief-facts { margin-top: 10px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.06); }
  .brief-facts .detail-label { margin-top: 0; }
  .brief-mini { list-style: none; }
  .brief-mini li { font-size: 14px; line-height: 1.55; color: var(--text-1); padding: 4px 0 4px 16px; position: relative; }
  .brief-mini li::before { content: '\u25B8'; position: absolute; left: 0; color: var(--text-3); font-size: 11px; }

  .bright-header { padding: 24px 20px 6px; }
  .bright-title { font-size: 22px; font-weight: 700; background: linear-gradient(135deg, #f59e0b, #fbbf24, #f97316); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; margin-bottom: 4px; }
  .bright-sub { font-size: 13px; color: var(--text-2); line-height: 1.5; }
  .bright-card { margin: 12px 20px; padding: 18px; background: linear-gradient(135deg, rgba(245,158,11,0.06), rgba(249,115,22,0.03)); border: 1px solid rgba(245,158,11,0.12); border-radius: 12px; cursor: pointer; -webkit-tap-highlight-color: transparent; transition: transform 0.1s; }
  .bright-card:active { border-color: rgba(245,158,11,0.25); transform: scale(0.99); }
  .bright-card .fact-title { font-size: 17px; background: linear-gradient(135deg, #fbbf24, #f59e0b); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .bright-card .fact-snippet { color: var(--text-1); }
  .bright-card .tag-confirmed { background: rgba(251,191,36,0.1); color: #fbbf24; }
  .bright-card .fact-detail { background: rgba(245,158,11,0.05); border-color: rgba(245,158,11,0.12); }
  .bright-card .fact-list li { color: var(--text-0); }
  .bright-card .detail-ctx { color: var(--text-1); }

  .live-banner { margin: 8px 20px 12px; padding: 14px 16px; background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.2); border-radius: 10px; cursor: pointer; position: relative; overflow: hidden; transition: transform 0.1s; }
  .live-banner:active { transform: scale(0.99); }
  .live-banner::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--red), transparent); animation: scanLine 2.5s linear infinite; }
  .live-indicator { display: inline-flex; align-items: center; gap: 6px; font-family: var(--mono); font-size: 9px; font-weight: 600; letter-spacing: 1.5px; color: var(--red); text-transform: uppercase; margin-bottom: 8px; }
  .live-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--red); animation: pulse 1.5s infinite; }
  .live-banner .fact-title { font-size: 16px; }
  .live-card { padding: 18px 20px; border-bottom: 1px solid rgba(239,68,68,0.1); cursor: pointer; transition: transform 0.1s; }
  .live-card:active { background: var(--bg-1); transform: scale(0.995); }

  .bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; background: rgba(6,6,10,0.94); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-top: 1px solid var(--border); display: flex; justify-content: space-around; padding: 8px 0 calc(8px + var(--safe-bottom)); z-index: 100; }
  .nav-btn { display: flex; flex-direction: column; align-items: center; gap: 3px; background: none; border: none; padding: 6px 12px; cursor: pointer; -webkit-tap-highlight-color: transparent; position: relative; min-width: 56px; transition: transform 0.1s; }
  .nav-btn:active { transform: scale(0.9); }
  .nav-icon { font-size: 20px; transition: transform 0.15s; }
  .nav-label { font-family: var(--sans); font-size: 10px; font-weight: 500; color: var(--text-3); }
  .nav-btn.active .nav-label { color: var(--text-0); }
  .nav-alert-dot { position: absolute; top: 2px; right: 8px; width: 6px; height: 6px; border-radius: 50%; background: var(--red); animation: pulse 1.5s infinite; }

  .empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 24px; text-align: center; }
  .empty-icon { font-size: 28px; opacity: 0.25; margin-bottom: 14px; }
  .empty-text { font-family: var(--mono); font-size: 11px; color: var(--text-3); letter-spacing: 0.3px; line-height: 1.8; }
  .loading-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; padding: 60px 20px; }
  .spinner { width: 22px; height: 22px; border: 2px solid var(--border); border-top-color: var(--blue); border-radius: 50%; animation: spin 0.7s linear infinite; margin-bottom: 14px; }
  .overlay { position: fixed; inset: 0; z-index: 40; background: rgba(0,0,0,0.4); }

  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeDown { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes scanLine { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
  .anim-in { animation: fadeUp 0.2s ease forwards; }

  @media (min-width: 768px) {
    .scroll-body { max-width: 640px; margin: 0 auto; width: 100%; }
    .bottom-nav { max-width: 640px; left: 50%; transform: translateX(-50%); border-radius: 16px 16px 0 0; }
    .top-header { max-width: 640px; margin: 0 auto; width: 100%; }
    .ch-dropdown-wrap { max-width: 640px; margin: 0 auto; width: 100%; }
  }
`;

// ── Components ──
function HeatBar({ value, size = "sm" }) {
  const pct = Math.max(15, Math.min(100, ((value - 40) / 60) * 100));
  const col = heatColor(value);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: size === "lg" ? 10 : 6, marginLeft: size === "sm" ? "auto" : 0, flex: size === "lg" ? 1 : "none" }}>
      <span style={{ fontFamily: "var(--mono)", fontSize: size === "lg" ? 16 : 10, fontWeight: 700, color: col, minWidth: size === "lg" ? 30 : 20 }}>{value}</span>
      <div style={{ width: size === "lg" ? "100%" : 56, height: size === "lg" ? 8 : 5, background: "#1a1a2a", borderRadius: 99, overflow: "hidden", position: "relative", flex: size === "lg" ? 1 : "none" }}>
        <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: pct + "%", borderRadius: 99, background: "linear-gradient(90deg, " + col + "88, " + col + ")", boxShadow: "0 0 " + (size === "lg" ? 8 : 4) + "px " + col + "66", transition: "width 0.5s ease" }} />
      </div>
    </div>
  );
}

function StatusTag({ status }) { return <span className={"tag " + (status === "Confirmed" ? "tag-confirmed" : "tag-developing")}>{status}</span>; }
function ConfText({ value }) { const c = value >= 90 ? "var(--green)" : value >= 75 ? "var(--amber)" : "var(--red)"; return <span style={{ fontFamily: "var(--mono)", fontSize: 9, fontWeight: 600, color: c }}>{value}%</span>; }

function SourceMeta({ fact }) {
  const sources = Array.isArray(fact.src) ? fact.src : [];
  return (
    <div className="fact-meta">
      {sources.map((s, i) => {
        const url = getSourceUrl(fact, i);
        return url ? <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="src-chip" onClick={e => e.stopPropagation()}>{s}{"\u00A0\u2197"}</a>
          : <span key={i} className="src-chip">{s}</span>;
      })}
      <span className="meta-time">{timeAgo(fact.created_at)}</span>
    </div>
  );
}

function FactDetail({ fact }) {
  const sources = Array.isArray(fact.src) ? fact.src : [];
  return (
    <div className="fact-detail anim-in">
      <div className="detail-label">Verified Facts</div>
      {Array.isArray(fact.facts) && <ul className="fact-list">{fact.facts.map((f, i) => <li key={i}>{f}</li>)}</ul>}
      <div className="detail-label">Context</div>
      <div className="detail-ctx">{fact.ctx || "No additional context."}</div>
      <div className="detail-label">Sources</div>
      <div className="detail-sources">
        {sources.map((s, i) => {
          const url = getSourceUrl(fact, i);
          const Tag = url ? "a" : "div";
          const props = url ? { href: url, target: "_blank", rel: "noopener noreferrer", onClick: e => e.stopPropagation() } : {};
          return <Tag key={i} className="detail-src-row" {...props}><span className="detail-src-name">{s}</span>{url && <span className="detail-src-arrow">{"\u2197"}</span>}</Tag>;
        })}
      </div>
      <div className="detail-label">Heat Score</div>
      <HeatBar value={fact.ht} size="lg" />
      <div className="detail-label">Intelligence</div>
      <div className="detail-intel">{fact.co}% CONFIDENCE {"\u00B7"} {fact.st?.toUpperCase()} {"\u00B7"} {fact.agent} {"\u00B7"} {timeAgo(fact.created_at)}</div>
      <button className="share-btn" onClick={e => { e.stopPropagation(); shareFact(fact); }}>{"\u{1F4E4}"} Share this story</button>
    </div>
  );
}

// ── Main App ──
export default function App() {
  const [facts, setFacts] = useState([]);
  const [explore, setExplore] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("top");
  const [activeCh, setActiveCh] = useState("world");
  const [expandedId, setExpandedId] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const scrollRef = useRef(null);
  const cardRefs = useRef({});

  const fetchAll = useCallback(async () => {
    try {
      const [fRes, eRes] = await Promise.all([
        supabase.from("Facts").select("*").order("id", { ascending: false }).limit(400),
        supabase.from("Explore").select("*").order("rank", { ascending: true }).limit(12),
      ]);
      if (fRes.data) setFacts(fRes.data);
      if (eRes.data) setExplore(eRes.data);
      setLastSync(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchAll();
    const ch = supabase.channel("fl-rt")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "Facts" }, p => setFacts(prev => [p.new, ...prev]))
      .on("postgres_changes", { event: "*", schema: "public", table: "Explore" }, () => {
        supabase.from("Explore").select("*").order("rank", { ascending: true }).limit(12).then(({ data }) => { if (data) setExplore(data); });
      }).subscribe();
    const poll = setInterval(fetchAll, 60000);
    return () => { supabase.removeChannel(ch); clearInterval(poll); };
  }, [fetchAll]);

  // v9.2: Smart toggle — scroll to new card, don't jump when closing
  const toggle = useCallback((id) => {
    setExpandedId(prev => {
      const next = prev === id ? null : id;
      if (next) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            const el = cardRefs.current[next];
            if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }, 50);
        });
      }
      return next;
    });
  }, []);

  const liveFacts = facts.filter(f => f.ch === "live-event");
  const brightFacts = dedup(facts.filter(f => f.ch === "bright")).slice(0, 12);
  const channelFacts = dedup(facts.filter(f => f.ch === activeCh)).slice(0, 20);
  const hasLive = liveFacts.length > 0;

  const chCounts = {};
  FEED_CHANNELS.forEach(c => { chCounts[c.id] = facts.filter(f => f.ch === c.id).length; });
  const activeMeta = FEED_CHANNELS.find(c => c.id === activeCh) || FEED_CHANNELS[0];

  const switchTab = t => { setTab(t); setExpandedId(null); setDropdownOpen(false); if (scrollRef.current) scrollRef.current.scrollTop = 0; };
  const selectChannel = chId => { setActiveCh(chId); setDropdownOpen(false); setExpandedId(null); if (scrollRef.current) scrollRef.current.scrollTop = 0; };

  const topBriefing = (() => {
    const seen = new Set(), out = [];
    for (const item of explore || []) {
      const full = facts.find(f => f.id === item.fact_id) || item;
      const k = topicKeyFromTitle(full?.title);
      if (!k || seen.has(k)) continue;
      seen.add(k); out.push({ item, full });
      if (out.length >= 6) break;
    }
    return out;
  })();

  const renderCard = (fact, cls, titleStyle) => (
    <div key={fact.id} ref={el => { cardRefs.current[fact.id] = el; }} className={(cls || "fact-card") + (expandedId === fact.id ? " expanded" : "")} onClick={() => toggle(fact.id)}>
      <div className="fact-tags"><StatusTag status={fact.st} /><ConfText value={fact.co} /><HeatBar value={fact.ht} /></div>
      <div className="fact-title" style={titleStyle}>{fact.title}</div>
      {expandedId !== fact.id && fact.ctx && <div className="fact-snippet">{fact.ctx}</div>}
      <SourceMeta fact={fact} />
      {expandedId === fact.id && <FactDetail fact={fact} />}
    </div>
  );

  return (
    <>
      <style>{CSS}</style>
      <div className="shell">
        <div className="top-header">
          <div className="brand">READ<span>ONLY</span></div>
          <div className="sync-badge"><span className="sync-dot" />{lastSync ? lastSync.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "..."}</div>
        </div>

        {tab === "feed" && (
          <div className="ch-dropdown-wrap">
            <button className="ch-dropdown-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <div className="ch-label-row"><span className="ch-color-dot" style={{ background: activeMeta?.color }} /><span>{activeMeta?.label}</span><span className="ch-fact-count">{chCounts[activeCh] || 0} facts</span></div>
              <span className={"ch-arrow " + (dropdownOpen ? "open" : "")}>{"\u25BE"}</span>
            </button>
            {dropdownOpen && <>
              <div className="overlay" onClick={() => setDropdownOpen(false)} />
              <div className="ch-dropdown-menu">{FEED_CHANNELS.map(ch => (
                <div key={ch.id} className={"ch-dropdown-item " + (activeCh === ch.id ? "active" : "")} onClick={() => selectChannel(ch.id)}>
                  <div className="ch-label-row"><span className="ch-color-dot" style={{ background: ch.color, width: 10, height: 10, borderRadius: "50%" }} /><span>{ch.label}</span></div>
                  <span className="ch-fact-count">{chCounts[ch.id] || 0}</span>
                </div>
              ))}</div>
            </>}
          </div>
        )}

        <div className="scroll-body" ref={scrollRef}>
          {loading ? <div className="loading-wrap"><div className="spinner" /><div className="empty-text">CONNECTING TO INTELLIGENCE FEED...</div></div> : <>

            {tab === "feed" && <>
              {hasLive && liveFacts.slice(0, 2).map(lf => (
                <div key={lf.id} className="live-banner" onClick={() => switchTab("live")}>
                  <div className="live-indicator"><span className="live-dot" />LIVE {lf.live_tag ? "\u00B7 " + lf.live_tag.toUpperCase().replace(/-/g, " ") : ""}</div>
                  <div className="fact-title" style={{ fontSize: 15 }}>{lf.title}</div>
                  <SourceMeta fact={lf} />
                </div>
              ))}
              {channelFacts.length > 0 ? channelFacts.map(f => renderCard(f)) : <div className="empty"><div className="empty-icon">{"\u25C9"}</div><div className="empty-text">No intelligence yet for {activeMeta?.label}.<br />Waiting for next engine cycle...</div></div>}
            </>}

            {tab === "top" && <>
              <div className="brief-head"><div className="brief-title">Top Briefing</div><div className="brief-sub">Ranked by velocity {"\u00B7"} deduped for clarity{lastSync ? " \u00B7 " + timeAgo(lastSync) : ""}</div></div>
              {topBriefing.length > 0 ? topBriefing.map(({ item, full }, idx) => {
                const mini = Array.isArray(full?.facts) ? full.facts.slice(0, 3) : [];
                const chMeta = FEED_CHANNELS.find(c => c.id === item.ch);
                return (
                  <div key={item.id} ref={el => { cardRefs.current[item.id] = el; }} className="brief-card anim-in" onClick={() => toggle(item.id)}>
                    <div className="brief-toprow">
                      <div className="brief-pill-row">
                        <span className="brief-rank">#{idx + 1}</span>
                        <span className="tag tag-ch" style={item.ch === "live-event" ? { background: "rgba(239,68,68,0.08)", color: "var(--red)" } : chMeta?.color ? { background: chMeta.color + "14", color: chMeta.color } : {}}>{item.ch === "live-event" ? item.live_tag || "live" : item.ch}</span>
                        <StatusTag status={item.st} /><ConfText value={item.co} />
                      </div>
                      <HeatBar value={item.ht} />
                    </div>
                    <div className="fact-title" style={{ fontSize: 18 }}>{item.title}</div>
                    {mini.length > 0 && expandedId !== item.id && <div className="brief-facts"><div className="detail-label">Key facts</div><ul className="brief-mini">{mini.map((f, i) => <li key={i}>{f}</li>)}</ul></div>}
                    <SourceMeta fact={full || item} />
                    {expandedId === item.id && full && <FactDetail fact={full} />}
                  </div>
                );
              }) : <div className="empty"><div className="empty-icon">{"\u25CE"}</div><div className="empty-text">Top stories not yet available.<br />Waiting for engine cycle...</div></div>}
            </>}

            {tab === "bright" && <>
              <div className="bright-header"><div className="bright-title">The Bright Side</div><div className="bright-sub">Breakthroughs, discoveries, and stories worth sharing.</div></div>
              {brightFacts.length > 0 ? brightFacts.map(f => renderCard(f, "bright-card anim-in")) : <div className="empty"><div className="empty-icon">{"\u2600"}</div><div className="empty-text">Good news is on the way.</div></div>}
            </>}

            {tab === "live" && <>
              <div className="section-label" style={{ paddingTop: 20, color: "var(--red)" }}>{"\u25CF"} Live Events</div>
              {liveFacts.length > 0 ? liveFacts.map(f => renderCard(f, "live-card", { fontSize: 19 })) : <div className="empty"><div className="empty-icon">{"\u26A1"}</div><div className="empty-text">No live events right now.</div></div>}
            </>}

            {tab === "mission" && <>
              <div className="section-label" style={{ paddingTop: 20 }}>Mission</div>
              <div className="brief-card anim-in" style={{ margin: "8px 20px" }}>
                <div className="fact-title" style={{ marginBottom: 10 }}>Why this exists</div>
                <ul className="fact-list">{MISSION.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
                <div style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--text-3)", marginTop: 12, letterSpacing: 0.3, lineHeight: 2 }}>{MISSION.footnote}</div>
              </div>
            </>}
          </>}
        </div>

        <div className="bottom-nav">
          <button className={"nav-btn " + (tab === "feed" ? "active" : "")} onClick={() => switchTab("feed")}>
            {hasLive && tab !== "feed" && <span className="nav-alert-dot" />}
            <span className="nav-icon" style={tab === "feed" ? { filter: "drop-shadow(0 0 4px rgba(59,130,246,0.5))" } : {}}>{tab === "feed" ? "\u25C9" : "\u25CB"}</span>
            <span className="nav-label">Feed</span>
          </button>
          <button className={"nav-btn " + (tab === "top" ? "active" : "")} onClick={() => switchTab("top")}>
            <span className="nav-icon" style={tab === "top" ? { filter: "drop-shadow(0 0 4px rgba(59,130,246,0.5))" } : {}}>{tab === "top" ? "\u25C8" : "\u25C7"}</span>
            <span className="nav-label">Top</span>
          </button>
          <button className={"nav-btn " + (tab === "bright" ? "active" : "")} onClick={() => switchTab("bright")}>
            <span className="nav-icon" style={tab === "bright" ? { color: "#fbbf24", filter: "drop-shadow(0 0 6px rgba(251,191,36,0.4))" } : {}}>{tab === "bright" ? "\u2726" : "\u2727"}</span>
            <span className="nav-label" style={tab === "bright" ? { color: "#fbbf24" } : {}}>Bright</span>
          </button>
          <button className={"nav-btn " + (tab === "mission" ? "active" : "")} onClick={() => switchTab("mission")}>
            <span className="nav-icon" style={tab === "mission" ? { filter: "drop-shadow(0 0 4px rgba(59,130,246,0.5))" } : {}}>{tab === "mission" ? "\u2B21" : "\u2B22"}</span>
            <span className="nav-label">Mission</span>
          </button>
          {hasLive && <button className={"nav-btn " + (tab === "live" ? "active" : "")} onClick={() => switchTab("live")}>
            {tab !== "live" && <span className="nav-alert-dot" />}
            <span className="nav-icon" style={{ color: tab === "live" ? "var(--red)" : "" }}>{"\u26A1"}</span>
            <span className="nav-label" style={tab === "live" ? { color: "var(--red)" } : {}}>Live</span>
          </button>}
        </div>
      </div>
    </>
  );
}
